import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../api";
import { toast } from "sonner";
import useMotivoAnulacionFactura from "../facturacion/useMotivoAnulacionFactura";
import { Banknote, Receipt, FileText, Download, Mail, RotateCcw, Loader2, Printer, X, Pencil, Check, XCircle, CheckCircle2 } from "lucide-react";
import { esCobroIS } from "../../utils/rejillaIS";
import { EST, EST_HUECO_JUSTIFICADO, EST_ERROR_NO_GENERADO, EST_ERROR_DESCONOCIDO, MET, MET_FULL } from "./estadosCobro";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

// 01-10-2026 · el catálogo de estados y de formas de pago vivía AQUÍ, y había
// otra tabla distinta en el panel de taquillas de la ficha: el mismo cobro se
// llamaba «Generado» o «Pendiente» según la pantalla por la que entrases.
// Ahora es uno solo y se importa. Si falta un estado, se añade ALLÍ.
const S = {
  iconBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "4px", border: "none", background: "none", cursor: "pointer", borderRadius: "4px", marginLeft: "3px", verticalAlign: "middle", minWidth: "24px", minHeight: "24px" },
  badge: { display: "inline-block", padding: "1px 6px", borderRadius: "3px", fontSize: "10px", fontWeight: 600, lineHeight: "16px" },
};

const RejillaAnualPagos = ({ clienteId, anyo: anyoExterno, onAnyoChange }) => {
  const { pedirMotivo, modalMotivo } = useMotivoAnulacionFactura();   // [Ann 30-10-2026] desplegable único de motivos
  const currentYear = new Date().getFullYear();
  const anyo = anyoExterno || currentYear;
  const setAnyo = (v) => { if (onAnyoChange) onAnyoChange(v); };
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cobrarModal, setCobrarModal] = useState(null); // { sub, mes } para cobro nuevo
  const [editarCobro, setEditarCobro] = useState(null); // { cobro_id, mes, metodo_actual }
  const [confirmarCobro, setConfirmarCobro] = useState(null); // { cobro_id, mes_nombre }
  const [confirmarMetodo, setConfirmarMetodo] = useState("efectivo");
  const [confirmarFecha, setConfirmarFecha] = useState(new Date().toISOString().split("T")[0]);
  const [cobrarCIModal, setCobrarCIModal] = useState(null); // { cobro_id, concepto, importe }
  const [ciMetodo, setCIMetodo] = useState("efectivo");
  const [ciFecha, setCIFecha] = useState(new Date().toISOString().split("T")[0]);
  const [ciEmitirFactura, setCIEmitirFactura] = useState(false);
  const [cobrarMetodo, setCobrarMetodo] = useState("efectivo");
  const [cobrarFecha, setCobrarFecha] = useState(new Date().toISOString().split("T")[0]);
  const [cobrarImporte, setCobrarImporte] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [editingImporte, setEditingImporte] = useState(null); // { subId, mes, value, original, sub }
  const [confirmImporte, setConfirmImporte] = useState(null);
  const [confirmAnular, setConfirmAnular] = useState(null); // { facturaId, cobro_id, mes }
  const [errorAnulacion, setErrorAnulacion] = useState(null); // { facturaId, msg, ts }
  const [confirmEmitir, setConfirmEmitir] = useState(null); // { cobro_id, mes, sub }
  const [previewModal, setPreviewModal] = useState(null); // { type: 'recibo'|'factura', id, html }
  const [previewHtml, setPreviewHtml] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(null);

  // GENERAR UN AÑO PARA ESTE CLIENTE (Ann 2026-10-09)
  // «necesito poder crear un año de un solo cliente en particular y así evito»
  // abrir el año entero. Caso real: una clienta paga 6 meses por adelantado y
  // algunos caen en 2027, un año que todavía no tiene ni una línea.
  // El SERVIDOR exige superadmin y devuelve 403 a cualquier otro: esto solo
  // evita enseñar un botón que no se podría usar.
  const { user } = useAuth();
  const esSuperAdmin = String(user?.role || "") === "admin";
  const [generarModal, setGenerarModal] = useState(null); // resultado del simulacro
  const [generando, setGenerando] = useState(false);

  // Paso 1 · SIMULACRO. No crea nada: pregunta al servidor qué haría.
  const pedirSimulacro = async () => {
    setGenerando(true);
    try {
      const res = await axios.post(
        `${API}/cobros/apertura-anual-cliente/${clienteId}?anyo=${anyo}&dry_run=true`
      );
      setGenerarModal(res.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = Array.isArray(d?.errores_globales) && d.errores_globales.length
        ? d.errores_globales.join(" · ")
        : (typeof d === "string" ? d : "No se ha podido comprobar el año");
      toast.error(msg);
    } finally {
      setGenerando(false);
    }
  };

  // Paso 2 · CREAR DE VERDAD. Lo que se muestra es lo que devuelve el servidor,
  // no una cuenta hecha aquí: si la regla cambia, el número cambia con ella.
  const generarAnyoDeVerdad = async () => {
    setGenerando(true);
    try {
      const res = await axios.post(
        `${API}/cobros/apertura-anual-cliente/${clienteId}?anyo=${anyo}&dry_run=false`
      );
      const creados = res.data?.total_cobros_creados ?? 0;
      const saltados = res.data?.total_cobros_saltados ?? 0;
      if (creados > 0) {
        toast.success(
          `Año ${anyo} generado: ${creados} ${creados === 1 ? "cuota" : "cuotas"}` +
          (saltados > 0 ? ` (${saltados} ya existían)` : "")
        );
      } else {
        // Un aviso que no dice el porqué obliga a adivinar. Los motivos los
        // manda el servidor (los pone el motor, mes a mes).
        const motivos = (res.data?.motivos_saltados || [])
          .map((m) => `${m.meses} ${m.meses === 1 ? "mes" : "meses"}: ${m.motivo}`)
          .join(" · ");
        toast.info(
          `No se ha creado ninguna cuota nueva en ${anyo}` +
          (motivos ? ` — ${motivos}` : "")
        );
      }
      setGenerarModal(null);
      await fetchRejilla();
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = Array.isArray(d?.errores_globales) && d.errores_globales.length
        ? d.errores_globales.join(" · ")
        : (typeof d === "string" ? d : "No se ha podido generar el año");
      toast.error(msg);
    } finally {
      setGenerando(false);
    }
  };

  const fetchRejilla = useCallback(async () => {
    if (!clienteId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cobros/rejilla-anual/${clienteId}?anyo=${anyo}`);
      setData(res.data);
    } catch (err) {
      console.error("Error cargando rejilla anual:", err);
    } finally {
      setLoading(false);
    }
  }, [clienteId, anyo]);

  useEffect(() => { fetchRejilla(); }, [fetchRejilla]);

  const [cobrarGastos, setCobrarGastos] = useState(0);
  const [gastosDevolucionDefault, setGastosDevolucionDefault] = useState(10);

  useEffect(() => {
    axios.get(`${API}/settings/gastos-gestion`)
      .then(res => setGastosDevolucionDefault(res.data.devolucion_recibos ?? 10))
      .catch(() => {});
  }, []);

  const openCobrar = (sub, mes) => {
    const esDevuelto = mes.estado === "devuelto";
    setCobrarModal({ sub, mes });
    setCobrarImporte(mes.importe);
    setCobrarGastos(esDevuelto ? gastosDevolucionDefault : 0);
    setCobrarMetodo(mes.metodo_pago || sub.metodo_cobro || "efectivo");
    setCobrarFecha(new Date().toISOString().split("T")[0]);
  };

  const handleCobrar = async () => {
    if (!cobrarModal) return;
    setProcessing(true);
    try {
      const { sub, mes } = cobrarModal;
      const importeTotal = cobrarImporte + (cobrarGastos || 0);
      const esRecobro = mes.estado === "devuelto" && mes.cobro_id;

      if (esRecobro) {
        // Recobro: actualiza cobro existente + cierra devolución en una sola llamada
        await axios.post(`${API}/cobros/${mes.cobro_id}/recobrar`, {
          fecha: cobrarFecha,
          metodo_pago: cobrarMetodo,
          importe: importeTotal,
          gastos: cobrarGastos || 0,
          devolucion_id: mes.devolucion?.id || null,
          nota: `Recobro ${cobrarMetodo} ${importeTotal}€${cobrarGastos > 0 ? ` (incluye ${cobrarGastos}€ gastos)` : ''}`,
        });
        toast.success(`Recobrado: ${mes.mes_nombre} ${anyo}${cobrarGastos > 0 ? ` (+${cobrarGastos}€ gastos)` : ''}`);
      } else {
        // Cobro nuevo estándar
        await axios.post(`${API}/cobros`, {
          tipo: "suscripcion",
          fecha: cobrarFecha,
          importe: importeTotal,
          metodo_pago: cobrarMetodo,
          cliente_id: clienteId,
          cliente_nombre: "",
          sede: sub.sede || "MUNTANER",
          origen: {
            tipo: "suscripcion",
            referencia_id: sub.suscripcion_id,
            concepto: sub.concepto,
            periodo: { mes: mes.mes, "año": anyo },
          },
          suscripcion_id: sub.suscripcion_id,
          periodo_cobro_mes: mes.mes,
          periodo_cobro_anyo: anyo,
          fecha_cobro_real: cobrarFecha,
        });
        toast.success(`Cobro registrado: ${mes.mes_nombre} ${anyo}${cobrarGastos > 0 ? ` (incluye ${cobrarGastos}€ gastos gestión)` : ''}`);
      }
      setCobrarModal(null);
      fetchRejilla();
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.message || err.message || "Error al registrar el cobro";
      console.error("[handleCobrar] error:", err);
      toast.error(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setProcessing(false);
    }
  };

  const handleCobrarCI = async () => {
    if (!cobrarCIModal || !cobrarCIModal.cobro_id) return;
    if (!(cobrarCIModal.importe > 0)) {
      toast.error("CI con importe 0 no entra en circuito de cobro");
      return;
    }
    setProcessing(true);
    try {
      const qs = new URLSearchParams({ metodo_pago: ciMetodo, fecha_cobro_real: ciFecha });
      if (ciEmitirFactura) qs.set("emitir_factura", "true");
      await axios.post(`${API}/cobros/${cobrarCIModal.cobro_id}/confirmar?${qs.toString()}`);
      toast.success("Cuota de inscripción cobrada");
      setCobrarCIModal(null);
      setCIEmitirFactura(false);
      fetchRejilla();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Error al cobrar la CI";
      toast.error(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setProcessing(false);
    }
  };

  const handleEmitirRecibo = async (cobro_id, mes, sub) => {
    try {
      const res = await axios.post(`${API}/recibos`, {
        cliente_id: clienteId,
        cliente_tipo: "user",
        concepto: mes.concepto,
        importe: mes.importe,
        metodo_pago: mes.metodo_pago || "efectivo",
        tipo_cobro: "cuota",
        referencia_id: cobro_id,
        sede: sub.sede || "MUNTANER",
      });
      const reciboId = res.data?.recibo?.id || res.data?.id || res.data?._id;
      if (reciboId) {
        await axios.post(`${API}/cobros/${cobro_id}/vincular-recibo`, { recibo_id: reciboId });
      }
      toast.success("Recibo emitido");
      fetchRejilla();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al emitir recibo");
    }
  };

  const handleEmitirFactura = (cobro_id, mes, sub) => {
    setConfirmEmitir({ cobro_id, mes, sub });
  };

  const handleEditarCobro = async () => {
    if (!editarCobro) return;
    setProcessing(true);
    try {
      await axios.put(`${API}/cobros/${editarCobro.cobro_id}`, {
        metodo_pago: editarCobro.metodo_nuevo,
        fecha_cobro_real: editarCobro.fecha_cobro_real,
      });
      toast.success("Cobro actualizado");
      setEditarCobro(null);
      fetchRejilla();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar cobro", { duration: 8000 });
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmEmitir = async () => {
    if (!confirmEmitir) return;
    const { cobro_id, mes, sub } = confirmEmitir;
    setConfirmEmitir(null);
    setProcessing(true);
    try {
      // AUTOPISTA_MARKER: única llamada al endpoint atómico backend.
      // El backend resuelve idempotencia, concepto canónico, emisión, vinculación
      // bidireccional y RF en una operación lógica. Si ya existía → abre PDF.
      const res = await axios.post(`${API}/cobros/${cobro_id}/emitir-factura`);
      const data = res.data || {};
      if (data.ya_existia) {
        toast(`Esta cuota ya tenía factura ${data.serie || ""}/${data.numero || "?"} (${data.estado}). Abriendo PDF.`,
          { duration: 8000, icon: "ℹ️" });
        await handlePreviewFactura(data.factura_id, mes, sub);
      } else {
        toast.success(`Factura ${data.serie || ""}/${data.numero || ""} emitida correctamente`);
      }
      fetchRejilla();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = (typeof detail === "string" ? detail : detail?.mensaje) || "Error al emitir factura";
      toast.error(msg, { duration: 10000 });
    } finally {
      setProcessing(false);
    }
  };

  // [Ann 2026-10-23] Deuda incobrable: queda en la rejilla en rojo oscuro, sale del granate, con motivo y auditoría.
  // [Ann 2026-10-30] «si un recibo es incobrable hay que poder retroceder la factura» · «un cobro que no va a
  // suceder no es pendiente en el Reporting»: UNA decisión en cascada (cobro · devolución · incidencia · avisos ·
  // factura con abono · ficha). Diálogo propio (sin prompt): motivo + casilla «retroceder la factura».
  const [incobrableModal, setIncobrableModal] = useState(null);   // { mes, motivo, retroceder }
  const marcarIncobrable = (mes) => {
    setIncobrableModal({ mes, motivo: "", retroceder: !!mes.factura_id });
  };
  const confirmarIncobrable = async () => {
    const { mes, motivo, retroceder } = incobrableModal || {};
    if (!mes) return;
    if ((motivo || "").trim().length < 5) { toast.error("Hace falta un motivo de al menos 5 letras"); return; }
    setProcessing(true);
    try {
      const r = await axios.post(`${API}/cobros/${mes.cobro_id}/incobrable`, { motivo: motivo.trim(), retroceder_factura: !!retroceder });
      const c = r?.data?.cascada || {};
      const partes = [`Cobro de ${mes.mes_nombre} incobrable`];
      if ((c.devoluciones || []).length) partes.push("devolución cerrada");
      if (c.abono_id) partes.push("factura retrocedida con abono");
      toast.success(partes.join(" · "));
      setIncobrableModal(null);
      fetchRejilla();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo marcar como incobrable");
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmarCobro = async () => {
    if (!confirmarCobro) return;
    try {
      const params = new URLSearchParams({ metodo_pago: confirmarMetodo, fecha_cobro_real: confirmarFecha });
      await axios.post(`${API}/cobros/${confirmarCobro.cobro_id}/confirmar?${params.toString()}`);
      toast.success(`Cobro ${confirmarCobro.mes_nombre} confirmado como ${confirmarMetodo}`);
      setConfirmarCobro(null);
      setConfirmarMetodo("efectivo");
      setConfirmarFecha(new Date().toISOString().split("T")[0]);
      fetchRejilla();
    } catch (err) {
      // El backend puede devolver detail como OBJETO (p.ej. guard IS 409:
      // {error, detalle, precio_is}). Pasar un objeto a toast.error rompe el
      // render de React y EXPULSA de la ficha → normalizar a texto legible.
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string"
        ? d
        : (d?.detalle || d?.error || err?.response?.data?.message || "Error al confirmar cobro");
      toast.error(msg, { duration: 8000 });
    }
  };

  const handleSaveImporte = (subId, mesNum, newImporte, originalImporte, sub, cobro_id) => {
    if (newImporte === originalImporte) {
      setEditingImporte(null);
      return;
    }
    setEditingImporte(null);
    setConfirmImporte({ subId, mes: mesNum, value: newImporte, original: originalImporte, sub, cobro_id });
  };

  const handleConfirmImporte = async () => {
    if (!confirmImporte) return;
    const { subId, mes: mesNum, value: newImporte, sub, cobro_id } = confirmImporte;
    try {
      if (cobro_id) {
        // Cobro ya existe (registrado/pendiente/hold) → PUT para editar importe
        let motivo = "Edición manual desde rejilla anual";
        if (subId === "__CI__" && Number(newImporte) === 0) {
          // PR-CI-EXENTO (Ann 2026-09-07): dejar la inscripción a 0 € = EXENTA → motivo obligatorio
          const m = window.prompt("Vas a dejar la cuota de inscripción EXENTA (0 €). Escribe el motivo (obligatorio):", "");
          if (!m || !m.trim()) { toast.error("Sin motivo no se puede dejar exenta la inscripción"); setConfirmImporte(null); return; }
          motivo = m.trim();
        }
        await axios.put(`${API}/cobros/${cobro_id}/importe`, {
          importe: newImporte,
          motivo,
        });
      } else {
        // Sin cobro → crear nuevo (comportamiento original)
        await axios.post(`${API}/cobros`, {
          tipo: "suscripcion",
          fecha: `${anyo}-${String(mesNum).padStart(2, "0")}-01`,
          importe: newImporte,
          metodo_pago: sub.metodo_cobro || "domiciliacion",
          cliente_id: clienteId,
          cliente_nombre: "",
          sede: sub.sede || "MUNTANER",
          origen: {
            tipo: "suscripcion",
            referencia_id: subId,
            concepto: `Cuota personalizada - ${newImporte.toFixed(2)} EUR`,
            periodo: { mes: mesNum, anyo: anyo },
          },
          suscripcion_id: subId,
          periodo_cobro_mes: mesNum,
          periodo_cobro_anyo: anyo,
        });
      }
      toast.success(`Importe actualizado: ${newImporte.toFixed(2)} EUR`);
      setConfirmImporte(null);
      fetchRejilla();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar el importe");
      setConfirmImporte(null);
    }
  };

  const handleConfirmAnular = async () => {
    if (!confirmAnular) return;

    // F2 · motivo OBLIGATORIO del catálogo (desplegable único · Ann 30-10-2026), queda en la cadena fiscal RF
    const motivoLimpio = await pedirMotivo(confirmAnular.facturaLabel || confirmAnular.facturaId || "");
    if (!motivoLimpio) return; // usuario canceló · no llamar

    setProcessing(true);
    try {
      // 1. Anular factura (crea rectificativa) · motivo en QUERY PARAM (no body)
      if (confirmAnular.facturaId) {
        await axios.post(
          `${API}/facturacion/facturas/${confirmAnular.facturaId}/anular?motivo=${encodeURIComponent(motivoLimpio)}`
        );
      }
      // 2. Anular cobro asociado (este endpoint sí espera motivo en body)
      if (confirmAnular.cobro_id) {
        await axios.post(`${API}/cobros/${confirmAnular.cobro_id}/anular`, {
          motivo: motivoLimpio
        });
      }
      toast.success("Factura anulada correctamente");
      setErrorAnulacion(null);
      setConfirmAnular(null);
      fetchRejilla();
    } catch (err) {
      // F1 · parser robusto del error backend (string/array/objeto/null)
      let msg = "Error desconocido al anular factura";
      const d = err?.response?.data?.detail;
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d)) {
        msg = d.map(e =>
          typeof e === "string" ? e :
          `${(e.loc||[]).filter(x => x!=="body" && x!=="query").join('.')}: ${e.msg||JSON.stringify(e)}`
        ).join(" · ");
      } else if (d && typeof d === "object") msg = JSON.stringify(d);
      else if (err?.message) msg = err.message;
      const status = err?.response?.status;
      if (status) msg = `[HTTP ${status}] ${msg}`;
      setErrorAnulacion({
        facturaId: confirmAnular.facturaId,
        msg,
        ts: new Date().toLocaleString("es-ES"),
      });
      toast.error(msg, { duration: 12000 });
    } finally {
      setProcessing(false);
    }
  };

  // --- Preview / Download / Email handlers ---
  // CRITICO: Preview de recibo y factura — abre modal con iframe PDF
  // Arquitectura idéntica a FacturasEmitidasB2BTab: iframe PDF + Imprimir + Descargar + Email
  const handlePreviewRecibo = async (reciboId, mes, sub) => {
    setPreviewModal({ type: "recibo", id: reciboId, mes_nombre: mes?.mes_nombre, sede: sub?.sede || "MPD", numero: mes?.recibo_numero || reciboId });
    setLoadingPreview(true);
    setPreviewHtml("");
    try {
      // Intentar cargar PDF del recibo
      const resp = await axios.get(`${API}/recibos/${reciboId}/pdf?t=${Date.now()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      setPreviewHtml(url); // Reutilizamos previewHtml como URL del PDF
    } catch {
      // Fallback: cargar HTML si no hay endpoint PDF
      try {
        const res = await axios.get(`${API}/recibos/${reciboId}/html`);
        setPreviewHtml("HTML:" + res.data.html);
      } catch (err) {
        toast.error("Error al cargar el recibo");
        setPreviewModal(null);
      }
    } finally {
      setLoadingPreview(false);
    }
  };

  const handlePreviewFactura = async (facturaId, mes, sub) => {
    setPreviewModal({ type: "factura", id: facturaId, mes_nombre: mes?.mes_nombre, sede: sub?.sede || "MPD", numero: mes?.factura_numero || facturaId });
    setLoadingPreview(true);
    setPreviewHtml("");
    try {
      const resp = await axios.get(`${API}/facturacion/facturas/${facturaId}/pdf?t=${Date.now()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      setPreviewHtml(url);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Error desconocido";
      toast.error(`Error factura: ${detail}`, { duration: 10000 });
      setPreviewModal(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handlePrint = () => {
    if (!previewHtml) return;
    if (previewHtml.startsWith("HTML:")) {
      const w = window.open("", "_blank");
      w.document.write(previewHtml.slice(5));
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 500);
    } else {
      // PDF: abrir en nueva pestaña e imprimir
      const w = window.open(previewHtml, "_blank");
      if (w) w.onload = () => w.print();
    }
  };

  // Imprimir la versión MUTUA (reembolso) de la factura: fisio + colegiado
  // en clases fijas MEFIR. Trae el PDF con reembolso=true y lo imprime.
  const handlePrintMutua = async () => {
    if (!previewModal?.id) return;
    try {
      const resp = await axios.get(`${API}/facturacion/facturas/${previewModal.id}/pdf?reembolso=true&t=${Date.now()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank");
      if (w) w.onload = () => w.print();
    } catch (e) { /* silencioso: no romper la rejilla */ }
  };

  const handleDownloadPDF = async (id, tipo = "factura") => {
    try {
      const endpoint = tipo === "recibo"
        ? `${API}/recibos/${id}/pdf?t=${Date.now()}`
        : `${API}/facturacion/facturas/${id}/pdf?t=${Date.now()}`;
      const resp = await axios.get(endpoint, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement("a");
      link.href = url;
      // Nombre descriptivo: Factura_SEDE_NUMERO_MES.pdf
      const sede = previewModal?.sede || "MPD";
      const numero = previewModal?.numero || id;
      const mes = previewModal?.mes_nombre || "";
      const label = tipo === "recibo" ? "Recibo" : "Factura";
      const fileName = `${label}_${sede}_${numero}${mes ? "_" + mes : ""}.pdf`.replace(/\s+/g, "_");
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Descarga iniciada");
    } catch (err) {
      toast.error("Error al descargar PDF");
    }
  };

  const handleSendEmailRecibo = async (reciboId) => {
    setSendingEmail(reciboId);
    try {
      const res = await axios.post(`${API}/recibos/${reciboId}/enviar-email`);
      toast.success(res.data.message || "Email enviado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar email");
    } finally {
      setSendingEmail(null);
    }
  };

  const handleSendEmailFactura = async (facturaId) => {
    setSendingEmail(facturaId);
    try {
      const res = await axios.post(`${API}/facturacion/facturas/${facturaId}/enviar-email`);
      toast.success(res.data.message || "Email enviado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar email");
    } finally {
      setSendingEmail(null);
    }
  };

  if (!clienteId) return null;

  // Build year range: from earliest year with data to currentYear + 1
  const years = [];
  // PR-REJILLA-CATALOGO (Ann 2026-05-18) · ya no hay estado "sin_cobrar",
  // se usa `tiene_cobro` para detectar meses con cobro real.
  const minYear = data && data.suscripciones.length > 0
    ? Math.min(currentYear - 1, ...data.suscripciones.flatMap(s => s.meses.filter(m => m.tiene_cobro).map(m => anyo)))
    : currentYear - 1;
  for (let y = Math.min(minYear, currentYear - 1); y <= currentYear + 1; y++) years.push(y);

  // ─────────────────────────────────────────────────────────────────────────
  // Microfix Ann 2026-05-13 · etiqueta contextual + bloque reutilizable para
  // pintar cuotas de inscripción asociadas a su etapa/ciclo correspondiente.
  // Reglas:
  //   - es_reinscripcion=false → "Cuota de inscripción inicial · {importe}€"
  //   - es_reinscripcion=true e importe>0 → "Cuota de inscripción reinscripción · {importe}€"
  //   - es_reinscripcion=true e importe=0 → "Cuota de inscripción reinscripción · 0€ · exenta"
  // El bloque se usa en dos sitios:
  //   - Dentro de cada ciclo de suscripción (asociadas por ciclo_index)
  //   - Sección secundaria "Inscripciones sin etapa" (fallback legacy)
  // ─────────────────────────────────────────────────────────────────────────
  // PR-CI-EXENTO (Ann 2026-09-07): «la rejilla debe mostrar exento y el motivo».
  // La marca la decide el backend (exenta al inscribir o editada a 0 €).
  const esExentaCI = (ins) => ins.exenta === true || (ins.importe ?? 0) === 0;
  const etiquetaInscripcion = (ins) => {
    const imp = (ins.importe ?? 0).toFixed(2);
    const base = ins.es_reinscripcion === true ? "Cuota de inscripción reinscripción" : "Cuota de inscripción inicial";
    if (esExentaCI(ins)) return `${base} · 0€ · EXENTA${ins.motivo ? ` (${ins.motivo})` : ""}`;
    return `${base} · ${imp}€`;
  };

  const renderInscripcionesBloque = (insList, headerLabel, keyPrefix) => {
    if (!insList || insList.length === 0) return null;
    const conMarcoPropio = headerLabel != null;
    const wrapStyle = conMarcoPropio
      ? { marginBottom: "12px", border: "1px solid #bbf7d0", borderRadius: "8px", overflow: "hidden" }
      : { borderBottom: "1px solid #bbf7d0", backgroundColor: "#f0fdf4" };
    return (
      <div style={wrapStyle}>
        {conMarcoPropio && (
          <div style={{ padding: "6px 10px", backgroundColor: "#f0fdf4", borderBottom: "1px solid #bbf7d0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "13px", fontWeight: 700, color: "#16a34a" }}>{headerLabel}</span>
          </div>
        )}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e8edf3" }}>
              <th style={{ padding: "5px 10px", textAlign: "left" }}>Concepto</th>
              <th style={{ padding: "5px 10px", textAlign: "right" }}>Importe</th>
              <th style={{ padding: "5px 10px", textAlign: "center", width: "70px" }}>Método</th>
              <th style={{ padding: "5px 10px", textAlign: "center" }}>Estado</th>
              <th style={{ padding: "5px 10px", textAlign: "center" }}>Fecha</th>
              <th style={{ padding: "5px 10px", textAlign: "right", width: "130px" }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {insList.map((ins, idx) => {
              // PR-REJILLA-CATALOGO (Ann 2026-05-18) · sin fallback inventado.
              // Si la inscripción tiene un estado no oficial → ERROR.
              const exentaCI = esExentaCI(ins);
              // Exenta → etiqueta propia «Exento» (no «Generado»); nunca cobrable ni editable desde aquí.
              const cfgIns = exentaCI && ins.estado === "registrado" ? EST.exento : (EST[ins.estado] || EST_ERROR_DESCONOCIDO);
              const puedeCobrarse = !exentaCI && ins.estado === "registrado" && ins.importe > 0 && ins.cobro_id;
              const puedeEditarImporte = !exentaCI && ins.estado === "registrado" && !!ins.cobro_id && !ins.recibo_id && !ins.factura_id;
              // Factura de una inscripción YA COBRADA que se cobró sin marcar «emitir
              // factura» (Ann, 19-09-2026: «no puedo imprimir ni enviar la factura de
              // la cuota de inscripción»). MISMO criterio que los meses para ofrecer
              // «Emitir factura»: cobro confirmado (cobrado = tiene_cobro && estado
              // "confirmado"), con cobro_id, importe > 0 y SIN factura. Exenta, nunca.
              const puedeEmitirFacturaCI = !exentaCI && ins.estado === "confirmado" && !!ins.cobro_id
                && (ins.importe ?? 0) > 0 && !ins.factura_id;
              const isEditing = editingImporte && editingImporte.subId === "__CI__" && editingImporte.cobro_id === ins.cobro_id;
              return (
                <tr key={`${keyPrefix}-${idx}`} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "5px 10px" }}>
                    <span style={{ fontWeight: 600 }}>{etiquetaInscripcion(ins)}</span>
                    {ins.concepto && ins.concepto !== "Cuota de Inscripción" && !exentaCI ? (
                      <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "1px" }}>{ins.concepto}</div>
                    ) : null}
                  </td>
                  <td style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600, whiteSpace: "nowrap" }}>
                    {isEditing ? (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "3px" }}>
                        <input type="number" step="0.01" autoFocus min="0"
                          value={editingImporte.value}
                          onChange={(e) => setEditingImporte({ ...editingImporte, value: parseFloat(e.target.value) || 0 })}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveImporte("__CI__", "CI", editingImporte.value, editingImporte.original, null, ins.cobro_id);
                            if (e.key === "Escape") setEditingImporte(null);
                          }}
                          style={{ width: "70px", padding: "2px 4px", fontSize: "13px", fontWeight: 600, textAlign: "right", border: "1px solid var(--cn-primary)", borderRadius: "3px" }} />
                        <button onClick={() => handleSaveImporte("__CI__", "CI", editingImporte.value, editingImporte.original, null, ins.cobro_id)}
                          style={{ ...S.iconBtn, color: "#16a34a", padding: "2px" }}>
                          <Check className="w-3 h-3" />
                        </button>
                      </span>
                    ) : (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", justifyContent: "flex-end" }}>
                        <span style={{ minWidth: "40px", textAlign: "right" }}>{ins.importe.toFixed(2)}</span>
                        <span style={{ width: "16px", display: "inline-flex", justifyContent: "center", flexShrink: 0 }}>
                          {puedeEditarImporte && (
                            <button onClick={() => setEditingImporte({ subId: "__CI__", mes: "CI", value: ins.importe, original: ins.importe, sub: null, cobro_id: ins.cobro_id })}
                              title="Editar importe (antes de cobrar)" style={{ ...S.iconBtn, color: "#9ca3af", padding: "2px" }}>
                              <Pencil className="w-3 h-3" />
                            </button>
                          )}
                        </span>
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "5px 10px", textAlign: "center", color: "#6b7280", fontSize: "12px" }}>
                    {ins.metodo_pago ? (MET[ins.metodo_pago] || ins.metodo_pago) : "—"}
                  </td>
                  <td style={{ padding: "5px 10px", textAlign: "center" }}>
                    <span style={{ ...S.badge, color: cfgIns.c, backgroundColor: cfgIns.bg, fontSize: "11px", padding: "2px 8px" }}>{cfgIns.l}</span>
                  </td>
                  <td style={{ padding: "5px 10px", textAlign: "center", color: "#6b7280", fontSize: "12px" }}>{ins.fecha ? ins.fecha.substring(0,10).split("-").reverse().join("/") : "—"}</td>
                  <td style={{ padding: "5px 10px", textAlign: "right" }}>
                    {puedeCobrarse && (
                      <button
                        onClick={() => {
                          setCobrarCIModal({ cobro_id: ins.cobro_id, concepto: ins.concepto, importe: ins.importe });
                          setCIMetodo(ins.metodo_pago || "efectivo");
                          setCIFecha(new Date().toISOString().split("T")[0]);
                          setCIEmitirFactura(false);
                        }}
                        style={{ ...S.iconBtn, color: "#16a34a" }}
                        title="Cobrar inscripción"
                      >
                        <Banknote className="w-4 h-4" />
                      </button>
                    )}
                    {ins.recibo_id ? (
                      <button
                        onClick={() => handlePreviewRecibo(ins.recibo_id, { mes_nombre: "Inscripción" }, { sede: "MPD" })}
                        title="Ver recibo"
                        style={{ ...S.iconBtn, color: "#16a34a" }}
                      >
                        <Receipt className="w-4 h-4" />
                      </button>
                    ) : (
                      <span title="Sin recibo" style={{ ...S.iconBtn, color: "#cbd5e1", cursor: "default" }}>
                        <Receipt className="w-4 h-4" />
                      </span>
                    )}
                    {ins.factura_id ? (
                      <button
                        onClick={() => handlePreviewFactura(ins.factura_id, { mes_nombre: "Inscripción" }, { sede: "MPD" })}
                        title="Ver factura"
                        style={{ ...S.iconBtn, color: "#16a34a" }}
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                    ) : puedeEmitirFacturaCI ? (
                      // Mismo camino que los meses: handleEmitirFactura → confirmación →
                      // POST /cobros/{cobro_id}/emitir-factura (atómico e idempotente).
                      // El modal lee concepto e importe: se pasan (sin ellos, se rompería).
                      <button
                        onClick={() => handleEmitirFactura(ins.cobro_id,
                          { mes_nombre: "Inscripción", concepto: ins.concepto || "Cuota de Inscripción", importe: ins.importe },
                          { sede: "MPD" })}
                        title="Emitir factura"
                        style={{ ...S.iconBtn, color: "#9ca3af" }}
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                    ) : (
                      <span title="Sin factura" style={{ ...S.iconBtn, color: "#cbd5e1", cursor: "default" }}>
                        <FileText className="w-4 h-4" />
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div style={{ marginBottom: "20px" }}>
      {modalMotivo}
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
        <h3 style={{ fontSize: "13px", fontWeight: 700, color: "var(--cn-text-main)", margin: 0, textTransform: "uppercase", letterSpacing: "0.5px" }}>
          Servicios Recurrentes
        </h3>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <select
          value={anyo}
          onChange={(e) => setAnyo(Number(e.target.value))}
          style={{ padding: "3px 8px", borderRadius: "4px", border: "1px solid var(--cn-border)", fontSize: "12px", fontWeight: 600 }}
        >
          {years.map((y) => (<option key={y} value={y}>{y}</option>))}
        </select>
          {esSuperAdmin && (
            <button
              onClick={pedirSimulacro}
              disabled={generando}
              title={`Crear las cuotas de ${anyo} para este cliente. Primero te enseña qué haría.`}
              style={{
                padding: "3px 10px", borderRadius: "4px",
                border: "1px solid var(--cn-border)", background: "none",
                fontSize: "12px", fontWeight: 600, cursor: generando ? "wait" : "pointer",
                color: "var(--cn-text-main)", whiteSpace: "nowrap",
              }}
            >
              {generando ? "Comprobando..." : `Generar año ${anyo}`}
            </button>
          )}
        </div>
      </div>

      {/* Panel persistente de error de anulación · F1 (decisión Ann 2026-05-11) */}
      {errorAnulacion && (
        <div style={{
          marginBottom: "12px",
          padding: "10px 12px",
          background: "#fef2f2",
          border: "1px solid #fca5a5",
          borderLeft: "4px solid #dc2626",
          borderRadius: "4px",
          color: "#7f1d1d",
          fontSize: "13px",
          lineHeight: 1.45,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "10px",
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, marginBottom: "4px" }}>
              Error al anular factura {errorAnulacion.facturaId ? `(id=${errorAnulacion.facturaId})` : ""}
            </div>
            <div style={{
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: "12px",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              background: "#fff",
              border: "1px solid #fecaca",
              borderRadius: "3px",
              padding: "6px 8px",
            }}>{errorAnulacion.msg}</div>
            <div style={{ fontSize: "11px", color: "#9b2c2c", marginTop: "6px" }}>
              {errorAnulacion.ts} · cierra este aviso pulsando ×
            </div>
          </div>
          <button
            onClick={() => setErrorAnulacion(null)}
            title="Cerrar mensaje"
            style={{
              background: "none",
              border: "1px solid #fca5a5",
              cursor: "pointer",
              color: "#7f1d1d",
              fontSize: "14px",
              lineHeight: 1,
              padding: "2px 8px",
              borderRadius: "3px",
            }}
          >×</button>
        </div>
      )}

      {loading && <p style={{ color: "var(--cn-text-muted)", fontSize: "12px" }}>Cargando...</p>}

      {!loading && data && data.suscripciones.length === 0 && (
        <div className="cn-panel" style={{ padding: "16px", textAlign: "center" }}>
          <p style={{ color: "var(--cn-text-muted)", fontSize: "12px", margin: 0 }}>
            No hay suscripciones activas para {anyo}
          </p>
        </div>
      )}

      {/* Microfix Ann 2026-05-13 · Cobros de inscripción asociados a su etapa
          se renderizan AHORA dentro de cada bloque de ciclo más abajo. Las
          inscripciones que no se pueden asociar (asociacion_etapa_tipo="sin_etapa")
          aparecen como sección secundaria al final · ver al cerrar el map. */}

      {!loading && data && data.suscripciones.map((sub, _ciclo_idx) => (
        <div key={sub.suscripcion_id} style={{ marginBottom: "12px", border: "1px solid var(--cn-border)", borderRadius: "8px", overflow: "hidden" }}>
          {/* Sub header */}
          <div style={{ padding: "6px 10px", backgroundColor: "#e0ecf8", borderBottom: "1px solid #c5d9f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "13px", fontWeight: 700, color: "#1e3a5f" }}>
              {sub.concepto}
            </span>
            <span style={{ fontSize: "12px", color: "var(--cn-text-muted)" }}>
              {sub.estado_suscripcion === "activa" ? "Activa" : sub.estado_suscripcion}
              {" | "}{MET[sub.metodo_cobro] || sub.metodo_cobro || "-"}
              {" | "}{sub.importe_base.toFixed(2)} EUR/mes
              {sub.sede ? ` | ${sub.sede}` : ""}
            </span>
          </div>

          {/* Microfix Ann 2026-05-13 · Inscripciones asociadas a este ciclo
              (ciclo_index === _ciclo_idx) se pintan ANTES de la tabla mensual. */}
          {renderInscripcionesBloque(
            (data?.inscripciones || []).filter((i) => i.ciclo_index === _ciclo_idx),
            null,
            `ciclo-${_ciclo_idx}`
          )}

          {/* Tabla */}
          <table style={{ width: "100%", fontSize: "13px", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#edf2fa" }}>
                <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 600, color: "#1e3a5f", width: "55px" }}>Mes</th>
                <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 600, color: "#1e3a5f" }}>Concepto</th>
                <th style={{ padding: "6px 10px", textAlign: "right", fontWeight: 600, color: "#1e3a5f", width: "70px" }}>Cuota</th>
                <th style={{ padding: "6px 10px", textAlign: "center", fontWeight: 600, color: "#1e3a5f", width: "95px" }}>Estado</th>
                <th style={{ padding: "6px 10px", textAlign: "center", fontWeight: 600, color: "#1e3a5f", width: "60px" }}>Pago</th>
                <th style={{ padding: "6px 10px", textAlign: "right", fontWeight: 600, color: "#1e3a5f", width: "140px" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {sub.meses.map((mes, mesIdx) => {
                // === Lógica de presentación del Estado (Ann 2026-05-18 · PR-REJILLA-ESTADOS-1 2026-06-01) ===
                // PR-REJILLA-ESTADOS-1: el backend puede devolver `estado_funcional` calculado
                // (read-only, no persiste en BD). Si viene, tiene prioridad visual sobre `estado`.
                // Backward-compatible: si backend no devuelve estado_funcional, usa estado.
                const estadoVisual = mes.estado_funcional ?? mes.estado;

                const esFuturo =
                  mes.tiene_cobro &&
                  mes.estado === "registrado" &&
                  mes.mes_numero > new Date().getMonth() + 1;

                // Re-cobrado: backend indica estado_funcional="re-cobrado"
                // O fallback backward-compat (confirmado + tuvo_devolucion sin estado_funcional)
                const esRecobrado =
                  estadoVisual === "re-cobrado" ||
                  (!mes.estado_funcional && mes.tiene_cobro && mes.estado === "confirmado" && mes.tuvo_devolucion);

                // Resolución de la gestión de recobro (Ann 2026-08-05): un cobro DEVUELTO que
                // Dirección cerró como error administrativo o condonado deja de ser "Devuelto" y
                // pasa a "Cancelado"/"Condonado", con la línea a 0 € y sin iconos de cobro.
                // Señal read-only en sepa_devoluciones.tipo_resolucion (no toca cobros/facturación).
                const _esDevuelto = mes.tiene_cobro && mes.estado === "devuelto";
                const _tipoRes = _esDevuelto && mes.devolucion ? (mes.devolucion.tipo_resolucion || "") : "";
                const resueltaRejilla =
                  _tipoRes === "no_exigible_fallo_administrativo" ? "cancelado" :
                  _tipoRes === "condonada_direccion" ? "condonado" :
                  _tipoRes === "incobrable_direccion" ? "incobrable" : null;

                let cfg;
                if (mes.error_rejilla === "COBRO_NO_GENERADO") {
                  cfg = EST_ERROR_NO_GENERADO;
                } else if (mes.error_rejilla === "ESTADO_DESCONOCIDO") {
                  cfg = EST_ERROR_DESCONOCIDO;
                } else if (resueltaRejilla) {
                  // Gestión cerrada (error admin / condonado) → Cancelado/Condonado, no "Devuelto"
                  cfg = EST[resueltaRejilla];
                } else if (esRecobrado) {
                  // Usa EST["re-cobrado"] en lugar de objeto inline · consistencia catálogo
                  cfg = EST["re-cobrado"];
                } else if (mes.tiene_cobro && estadoVisual && EST[estadoVisual]) {
                  cfg = EST[estadoVisual];
                } else if (!mes.tiene_cobro && mes.justificacion_sin_cobro) {
                  cfg = EST_HUECO_JUSTIFICADO;
                } else if (mes.tiene_cobro && estadoVisual) {
                  // Cobro con estado fuera del catálogo ampliado: ERROR (no fallback)
                  cfg = EST_ERROR_DESCONOCIDO;
                } else {
                  // Defensivo: sin error explícito, sin cobro y sin justificación
                  // → tratar como hueco neutro
                  cfg = EST_HUECO_JUSTIFICADO;
                }

                const cobrado = mes.tiene_cobro && mes.estado === "confirmado";
                const sinCobrar = !mes.tiene_cobro;
                const devuelto = mes.tiene_cobro && mes.estado === "devuelto";
                const pendiente = mes.tiene_cobro && mes.estado === "pendiente";

                // IS detection delegada a helper puro `esCobroIS` (PR-RejillaIS-1
                // · Ann 2026-05-26). Regla aprobada: la señal por concepto
                // (CUOTA IS / INCIDENCIA DE SALUD) ya NO exige es_mixto===true,
                // que ocultaba IS legacy con cobros tipo='suscripcion'.
                // Caso testigo: Rosa Maria marzo 2026 (origen.concepto incluye
                // "IS Incidencia de Salud" pero es_mixto ausente · ahora sí IS).
                // Helper en frontend/src/utils/rejillaIS.js · testeado en
                // __tests__/rejillaIS.test.js
                const tc = mes.tramos_calculo || [];
                const tcServ = tc.filter(t => t.tipo === "servicio");
                const tcInc = tc.filter(t => t.tipo === "incidencia");
                const tieneTramos = tc.length === 4;
                const esIS = esCobroIS(mes);

                // Marca de cambio de criterio (nueva etapa dentro del mismo ciclo)
                const esCambio = mes.cambio_etapa === true;
                // Cambio específico de SEDE (Ann 2026-05-20) · línea visual aparte.
                const esCambioSede = esCambio && mes.cambio_tipo === "sede";

                // Determinar tipo y formato de la línea naranja
                let isConcepto = "";
                let isImporte = mes.importe;
                if (esIS) {
                  const semNorm = tcServ.length;
                  const semIS = tcInc.length;

                  // REGLA GLOBAL CRM IS — concepto visible canónico:
                  //   Mes 100% IS  → "Cuota IS – {Mes} {Año}"
                  //   Mes mixto    → "Cuota IS – {Mes} {Año} · Proporción: {sem IS} + {sem normales}"
                  // Si el cobro YA trae un concepto canónico (backend nuevo), lo respetamos tal cual.
                  const conceptoBackendCanonico = (mes.origen_concepto || "").startsWith("Cuota IS –");
                  if (conceptoBackendCanonico) {
                    isConcepto = mes.origen_concepto;
                  } else if (tieneTramos && semNorm > 0 && semIS > 0) {
                    // Mes mixto: construir Proporción en orden IS → normales
                    const partes = [];
                    if (semIS) partes.push(`${semIS} semana${semIS !== 1 ? "s" : ""} IS`);
                    if (semNorm) partes.push(`${semNorm} semana${semNorm !== 1 ? "s" : ""} normal${semNorm !== 1 ? "es" : ""}`);
                    isConcepto = `Cuota IS – ${mes.mes_nombre} ${anyo} · Proporción: ${partes.join(" + ")}`;
                  } else {
                    // Mes completo IS o fallback sin tramos detallados
                    isConcepto = `Cuota IS – ${mes.mes_nombre} ${anyo}`;
                  }
                }

                const bgRow = mesIdx % 2 === 0 ? "#ffffff" : "#f0f4fa";
                // Prioridad visual del fondo: IS > cambio_etapa > alternancia normal
                const bgFinal = esIS ? "var(--is-bg-soft)" : (esCambio ? "#dbeafe" : bgRow);

                return (
                  <React.Fragment key={mes.mes}>
                  {/* Cambio de SEDE (Ann 2026-05-20) · línea visual dedicada
                      ANTES de la fila del mes inicial de la etapa nueva.
                      Solo cuando cambio_tipo='sede' (no se muestra para cambios
                      de cuota/horario/método, que conservan el borde azul). */}
                  {esCambioSede && (
                    <tr style={{ backgroundColor: "#eff6ff", borderTop: "2px solid #1d4ed8" }}>
                      <td colSpan={6} style={{
                        padding: "6px 10px",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#1e40af",
                        textAlign: "center",
                        letterSpacing: "0.02em",
                      }}>
                        📍 Cambio de sede: {(mes.sede_origen || "?").toString()} → {(mes.sede_destino || "?").toString()}
                      </td>
                    </tr>
                  )}
                  {/* Línea original: tachada en gris si es IS; con borde sup. azul si inicia nueva etapa del ciclo */}
                  <tr style={{ borderTop: (esCambio && !esCambioSede) ? "2px solid #3b82f6" : undefined, borderBottom: esIS ? "none" : "1px solid #e8edf3", backgroundColor: bgFinal, opacity: esIS ? 0.45 : 1 }}>
                    <td style={{ padding: "5px 10px", fontWeight: 600 }}>{mes.mes_nombre.slice(0, 3)}</td>
                    <td style={{ padding: "5px 10px", color: esIS ? "#94a3b8" : "#374151", textDecoration: esIS ? "line-through" : "none" }}>
                      {(() => {
                        const bracketMatch = (mes.concepto || "").match(/\[([^\]]+)\]/);
                        const mainText = bracketMatch ? mes.concepto.replace(/\s*\[[^\]]+\]/, "") : mes.concepto;
                        return (<>
                          {mainText}
                          {bracketMatch && <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: "1px" }}>{bracketMatch[1]}</div>}
                        </>);
                      })()}
                    </td>
                    <td style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600, whiteSpace: "nowrap" }}>
                      {editingImporte && editingImporte.subId === sub.suscripcion_id && editingImporte.mes === mes.mes ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "3px" }}>
                          <input type="number" step="0.01" autoFocus
                            value={editingImporte.value}
                            onChange={(e) => setEditingImporte({ ...editingImporte, value: parseFloat(e.target.value) || 0 })}
                            onKeyDown={(e) => { if (e.key === "Enter") handleSaveImporte(sub.suscripcion_id, mes.mes, editingImporte.value, editingImporte.original, editingImporte.sub, editingImporte.cobro_id); if (e.key === "Escape") setEditingImporte(null); }}
                            style={{ width: "70px", padding: "2px 4px", fontSize: "13px", fontWeight: 600, textAlign: "right", border: "1px solid var(--cn-primary)", borderRadius: "3px" }} />
                          <button onClick={() => handleSaveImporte(sub.suscripcion_id, mes.mes, editingImporte.value, editingImporte.original, editingImporte.sub, editingImporte.cobro_id)} style={{ ...S.iconBtn, color: "#16a34a", padding: "2px" }}>
                            <Check className="w-3 h-3" />
                          </button>
                        </span>
                      ) : (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", justifyContent: "flex-end" }}>
                          {/* Importe en bloque propio: ancho mínimo + alineación derecha → posición vertical estable */}
                          <span style={{ minWidth: "40px", textAlign: "right" }}>{resueltaRejilla ? (0).toFixed(2) : mes.importe.toFixed(2)}</span>
                          {/* Carril del lápiz: ancho fijo SIEMPRE reservado (esté o no el botón) → no desplaza el importe */}
                          <span style={{ width: "16px", display: "inline-flex", justifyContent: "center", flexShrink: 0 }}>
                            {(sinCobrar || mes.estado === "registrado" || mes.estado === "pendiente" || mes.estado === "hold") && (
                              <button onClick={() => setEditingImporte({ subId: sub.suscripcion_id, mes: mes.mes, value: mes.importe, original: mes.importe, sub, cobro_id: mes.cobro_id })}
                                title="Editar importe" style={{ ...S.iconBtn, color: "#9ca3af", padding: "2px" }}>
                                <Pencil className="w-3 h-3" />
                              </button>
                            )}
                          </span>
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "5px 10px", textAlign: "center" }}>
                      <span style={{ ...S.badge, color: cfg.c, backgroundColor: cfg.bg, fontSize: "11px", padding: "2px 8px" }}>{cfg.l}</span>
                      {mes.estado === "hold" && (
                        <span
                          title="No entra en la próxima remesa SEPA"
                          style={{ ...S.badge, marginLeft: 4, color: "#6b7280", backgroundColor: "#f3f4f6", fontSize: "9px", padding: "1px 5px", border: "1px solid #d1d5db", fontWeight: 500 }}
                        >
                          fuera de remesa
                        </span>
                      )}
                      {mes.tuvo_devolucion && mes.estado !== "devuelto" && (
                        <span
                          title={(() => {
                            const h = (mes.historial_devoluciones || []).slice(-1)[0] || {};
                            const dev = h.fecha ? `Devuelto ${h.fecha}${h.codigo_banco ? ` (${h.codigo_banco})` : ''}` : 'Devuelto';
                            const rec = h.recobrado_en ? ` · Recobrado ${h.recobrado_en}${h.recobrado_metodo ? ` ${h.recobrado_metodo}` : ''}` : '';
                            return dev + rec;
                          })()}
                          style={{ ...S.badge, marginLeft: 4, color: "#7f1d1d", backgroundColor: "#fee2e2", fontSize: "9px", padding: "1px 5px", border: "1px solid #fca5a5" }}
                        >
                          DEV→OK
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "5px 10px", textAlign: "center", color: "#6b7280" }}>
                      {mes.metodo_pago ? MET[mes.metodo_pago] || mes.metodo_pago : "-"}
                    </td>
                    <td style={{ padding: "5px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                      {/* BILLETE: gris=cobrar, verde+lapiz=editar cobro */}
                      {sinCobrar && (
                        <button onClick={() => openCobrar(sub, mes)} title="Cobrar" style={{ ...S.iconBtn, color: "#9ca3af" }}>
                          <Banknote className="w-4 h-4" />
                        </button>
                      )}
                      {(mes.estado === "registrado" || mes.estado === "hold") && mes.cobro_id && (
                        <button onClick={() => { setConfirmarCobro({ cobro_id: mes.cobro_id, mes_nombre: mes.mes_nombre }); setConfirmarMetodo(mes.metodo_pago || "efectivo"); }}
                          title="Confirmar cobro" style={{ ...S.iconBtn, color: "#2563eb" }}>
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                      )}
                      {/* [Ann 2026-10-23] «marcarla de algún modo en la rejilla como incobrable» */}
                      {(mes.estado === "registrado" || mes.estado === "hold" || mes.estado === "devuelto" || mes.estado === "incobrable" || (mes.estado === "anulado" && mes.anulado_a_mano)) && mes.cobro_id && (
                        <button onClick={() => marcarIncobrable(mes)} data-testid="btn-incobrable"
                          title={mes.estado === "incobrable" ? "Completar incobrable: cerrar devolución y retroceder la factura" : "Marcar incobrable (la empresa renuncia a cobrarlo)"} style={{ ...S.iconBtn, color: "#7f1d1d" }}>
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                      {devuelto && !resueltaRejilla && (
                        <>
                          <button onClick={() => openCobrar(sub, mes)} title="Re-cobrar" style={{ ...S.iconBtn, color: "#dc2626" }}>
                            <Banknote className="w-4 h-4" />
                          </button>
                          {mes.devolucion && (
                            <button
                              onClick={() => {
                                // Enlace directo a la gestión de recobro (Ann 2026-08-07): guarda el id
                                // de la devolución y navega a Contable → Devoluciones, que la abre sola.
                                try { sessionStorage.setItem("gestor_dev_abrir", mes.devolucion.id); } catch (e) { /* noop */ }
                                toast.info("Abriendo la gestión del recobro…");
                                navigate("/admin?tab=contable_agrupada");
                              }}
                              title="Ir a la gestión del recobro"
                              style={{ ...S.iconBtn, color: "#dc2626" }}>
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          )}
                          {mes.devolucion && (
                            <span title={mes.devolucion.motivo} style={{ fontSize: "9px", padding: "1px 4px", borderRadius: "3px", backgroundColor: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", fontWeight: 700, marginLeft: "2px" }}>
                              {mes.devolucion.codigo}
                            </span>
                          )}
                        </>
                      )}
                      {/* Gestión de recobro cerrada (error admin/condonado): sin iconos de cobro,
                          solo un icono que resume la gestión (Ann 2026-08-05). */}
                      {devuelto && resueltaRejilla && mes.devolucion && (
                        <button
                          onClick={() => {
                            // Enlace directo a la gestión de recobro (Ann 2026-08-07): guarda el id
                            // de la devolución y navega a Contable → Devoluciones, que la abre sola.
                            try { sessionStorage.setItem("gestor_dev_abrir", mes.devolucion.id); } catch (e) { /* noop */ }
                            toast.info("Abriendo la gestión del recobro…");
                            navigate("/admin?tab=contable_agrupada");
                          }}
                          title={
                            (resueltaRejilla === "cancelado" ? "Cancelado (error administrativo)" : "Condonado por Dirección")
                            + (mes.devolucion.cierre_motivo ? ` · ${mes.devolucion.cierre_motivo}` : "")
                            + " · ver gestión del recobro"
                          }
                          style={{ ...S.iconBtn, color: "#475569" }}>
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      )}
                      {/* PR-REJILLA-ESTADOS-1: "En remesa" solo para pendiente puro.
                          Si estadoVisual=remesado/re-remesado → el badge ya lo indica. */}
                      {pendiente && estadoVisual === "pendiente" && (
                        <span style={{ fontSize: "11px", color: "#d97706" }}>En remesa</span>
                      )}
                      {cobrado && (
                        <>
                          {/* Billete verde con lapiz = editar cobro */}
                          <button onClick={() => setEditarCobro({ cobro_id: mes.cobro_id, mes, metodo_actual: mes.metodo_pago, metodo_nuevo: mes.metodo_pago, fecha_cobro_real: mes.fecha_cobro_real || "" })}
                            title="Editar cobro" style={{ ...S.iconBtn, color: "#16a34a", position: "relative" }}>
                            <span style={{ position: "relative", display: "inline-flex" }}>
                              <Banknote className="w-4 h-4" />
                              <Pencil className="w-2 h-2" style={{ position: "absolute", bottom: "-1px", left: "-1px", color: "#16a34a", backgroundColor: "#fff", borderRadius: "2px" }} />
                            </span>
                          </button>
                          {/* Recibo: gris=emitir, verde=ver — solo si cobrado (decisión Ann 2026-05-02) */}
                          {!mes.recibo_id ? (
                            <button onClick={() => handleEmitirRecibo(mes.cobro_id, mes, sub)} title="Emitir recibo" style={{ ...S.iconBtn, color: "#9ca3af" }}>
                              <Receipt className="w-4 h-4" />
                            </button>
                          ) : (
                            <button onClick={() => handlePreviewRecibo(mes.recibo_id, mes, sub)} title="Ver recibo" style={{ ...S.iconBtn, color: "#16a34a" }}>
                              <Receipt className="w-4 h-4" />
                            </button>
                          )}
                        </>
                      )}
                      {/* Factura (decisión Ann 2026-05-02): si la factura YA está
                         emitida, mostrar verde+rojo SIEMPRE (cobrado o no). Esto
                         refleja que el lote mensual emite factura por anticipado.
                         Si NO hay factura, el botón gris "Emitir factura" solo
                         aparece cuando el cobro está cobrado (no se puede emitir
                         sobre un cobro aún no confirmado). */}
                      {mes.factura_id ? (
                        <>
                          <button onClick={() => handlePreviewFactura(mes.factura_id, mes, sub)} title="Ver factura" style={{ ...S.iconBtn, color: "#16a34a" }}>
                            <FileText className="w-4 h-4" />
                          </button>
                          <button onClick={() => setConfirmAnular({ facturaId: mes.factura_id, cobro_id: mes.cobro_id, mes })}
                            title="Anular factura" style={{ ...S.iconBtn, color: "#dc2626", position: "relative" }}>
                            <span style={{ position: "relative", display: "inline-flex" }}>
                              <FileText className="w-4 h-4" style={{ opacity: 0.5 }} />
                              <XCircle className="w-3 h-3" style={{ position: "absolute", bottom: "-2px", left: "-2px", color: "#dc2626", backgroundColor: "#fff", borderRadius: "50%" }} />
                            </span>
                          </button>
                        </>
                      ) : cobrado ? (
                        <button onClick={() => handleEmitirFactura(mes.cobro_id, mes, sub)} title="Emitir factura" style={{ ...S.iconBtn, color: "#9ca3af" }}>
                          <FileText className="w-4 h-4" />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                  {/* Línea IS naranja debajo de la original */}
                  {esIS && (
                    <tr style={{ borderBottom: "1px solid #e8edf3", backgroundColor: "var(--is-bg-soft)" }}>
                      <td style={{ padding: "3px 10px" }}></td>
                      <td colSpan="3" style={{ padding: "3px 10px", color: "#ea580c", fontSize: "12px", fontWeight: 600 }}>
                        {isConcepto}
                      </td>
                      <td style={{ padding: "3px 10px", textAlign: "right", fontWeight: 700, color: "#ea580c", fontSize: "12px" }}>
                        {isImporte.toFixed(2)}€
                      </td>
                      <td></td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {/* FASE 3 (Ann 2026-05-14) · El resumen de totales era GLOBAL pero
              se renderizaba DENTRO de este bloque .map(), repitiéndose en
              cada ciclo y dando la falsa impresión de "resumen del ciclo".
              Ahora se imprime UNA SOLA VEZ al final, fuera del .map(). */}
        </div>
      ))}

      {/* Microfix Ann 2026-05-13 · Sección fallback "Inscripciones sin etapa"
          para cobros tipo=inscripcion legacy sin suscripcion_id y que tampoco
          casan por fecha contra ningún ciclo cargado. NO se ocultan. */}
      {!loading && renderInscripcionesBloque(
        (data?.inscripciones || []).filter((i) => i.asociacion_etapa_tipo === "sin_etapa"),
        "Inscripciones sin etapa",
        "sin-etapa"
      )}

      {/* FASE 3 (Ann 2026-05-14) · Resumen GLOBAL único del año.
          Antes se repetía dentro de cada bloque de ciclo (engañoso).
          Ahora:
            - "Cobrado"   = solo cobros confirmados (dinero real)
            - "Generado"  = cobros registrados/generados (NO cobrados aún)
            - "Pendiente" = cobros pendientes (típicamente SEPA en proceso)
            - "Sin cobrar" = meses sin cobro vivo */}
      {!loading && data && data.totales && (
        <div style={{
          marginTop: "12px",
          padding: "10px 14px",
          border: "1px solid var(--cn-border)",
          borderRadius: "8px",
          backgroundColor: "#f8fafc",
          fontSize: "12px",
          color: "var(--cn-text-muted)",
        }}>
          <div style={{ fontSize: "11px", fontWeight: 700, color: "#1e3a5f",
                          marginBottom: "6px", textTransform: "uppercase",
                          letterSpacing: "0.5px" }}>
            Resumen del año {anyo}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
            <span>Cobrado: <strong style={{ color: "#16a34a" }}>{(data.totales.cobrado ?? 0).toFixed(2)} €</strong></span>
            <span>Generado: <strong style={{ color: "#0284c7" }}>{(data.totales.registrado ?? 0).toFixed(2)} €</strong></span>
            <span>Facturado: <strong style={{ color: "#7c3aed" }}>{(data.totales.facturado ?? 0).toFixed(2)} €</strong></span>
            <span>Remesado: <strong style={{ color: "#0891b2" }}>{(data.totales.remesado ?? 0).toFixed(2)} €</strong></span>
            <span>Pend SEPA: <strong style={{ color: "#d97706" }}>{(data.totales.pendiente ?? 0).toFixed(2)} €</strong></span>
            {(data.totales.errores ?? 0) > 0 && (
              <span>⚠️ Errores rejilla: <strong style={{ color: "#dc2626" }}>{data.totales.errores}</strong></span>
            )}
          </div>
          <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: "6px" }}>
            "Cobrado" = solo cobros confirmados. "Generado" = cobros creados aún no cobrados.
            Mes sin cobro real se muestra como "—" si está justificado; "⚠️ ERROR" si debería existir.
          </div>
        </div>
      )}

      {/* MODAL CONFIRMAR COBRO EN EFECTIVO */}
      {incobrableModal && (
        <div data-testid="modal-incobrable" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
          onClick={() => !processing && setIncobrableModal(null)}>
          <div style={{ background: "#fff", borderRadius: "12px", width: "460px", maxWidth: "95vw", padding: "22px", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "#7f1d1d" }}>
              Marcar incobrable · {incobrableModal.mes.mes_nombre} ({incobrableModal.mes.importe ?? ""} €)
            </h3>
            <p style={{ margin: "8px 0 14px", fontSize: "13px", color: "#4b5563" }}>
              La empresa renuncia a cobrarlo. Deja de ser deuda y de salir como pendiente en el Reporting.
              {incobrableModal.mes.estado === "devuelto" && " El recibo devuelto se cierra como incobrable."}
            </p>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#374151" }}>Motivo (mínimo 5 letras)</label>
            <textarea data-testid="txt-motivo-incobrable" rows={3} value={incobrableModal.motivo}
              onChange={(e) => setIncobrableModal({ ...incobrableModal, motivo: e.target.value })}
              style={{ width: "100%", marginTop: "4px", padding: "8px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px", boxSizing: "border-box" }} />
            {incobrableModal.mes.factura_id ? (
              <label style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "12px", fontSize: "13px", color: "#374151" }}>
                <input type="checkbox" data-testid="chk-retroceder-factura" checked={!!incobrableModal.retroceder}
                  onChange={(e) => setIncobrableModal({ ...incobrableModal, retroceder: e.target.checked })} />
                Retroceder la factura{incobrableModal.mes.factura_numero ? ` ${incobrableModal.mes.factura_numero}` : ""} (se emite su abono)
              </label>
            ) : (
              <p style={{ margin: "12px 0 0", fontSize: "12px", color: "#6b7280" }}>Este cobro no tiene factura emitida: no hay nada que retroceder.</p>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "18px" }}>
              <button type="button" onClick={() => setIncobrableModal(null)} disabled={processing}
                style={{ padding: "8px 14px", border: "1px solid #d1d5db", borderRadius: "6px", background: "#fff", cursor: "pointer" }}>Cancelar</button>
              <button type="button" data-testid="btn-confirmar-incobrable" onClick={confirmarIncobrable} disabled={processing || (incobrableModal.motivo || "").trim().length < 5}
                style={{ padding: "8px 14px", border: "none", borderRadius: "6px", background: "#7f1d1d", color: "#fff", fontWeight: 600, cursor: "pointer", opacity: (incobrableModal.motivo || "").trim().length < 5 ? 0.5 : 1 }}>
                {processing ? "..." : "Marcar incobrable"}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmarCobro && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
          onClick={() => setConfirmarCobro(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: "12px", padding: "24px", width: "340px", boxShadow: "0 8px 30px rgba(0,0,0,0.2)" }}>
            <h3 style={{ margin: "0 0 16px", fontSize: "15px", fontWeight: 700, color: "#1e293b" }}>Confirmar cobro — {confirmarCobro.mes_nombre}</h3>
            <div style={{ marginBottom: "12px" }}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "4px" }}>Método de pago</label>
              <select value={confirmarMetodo} onChange={(e) => setConfirmarMetodo(e.target.value)}
                style={{ width: "100%", padding: "8px", fontSize: "13px", border: "1px solid #e2e8f0", borderRadius: "6px" }}>
                <option value="efectivo">Efectivo</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="transferencia">Transferencia</option>
                <option value="bizum">Bizum</option>
              </select>
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "4px" }}>Fecha de cobro</label>
              <input type="date" value={confirmarFecha} onChange={(e) => setConfirmarFecha(e.target.value)}
                style={{ width: "100%", padding: "8px", fontSize: "13px", border: "1px solid #e2e8f0", borderRadius: "6px" }} />
            </div>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button onClick={() => setConfirmarCobro(null)}
                style={{ padding: "8px 16px", fontSize: "13px", border: "1px solid #e2e8f0", borderRadius: "6px", background: "#fff", cursor: "pointer" }}>Cancelar</button>
              <button onClick={handleConfirmarCobro}
                style={{ padding: "8px 16px", fontSize: "13px", border: "none", borderRadius: "6px", background: "#16a34a", color: "#fff", fontWeight: 600, cursor: "pointer" }}>Confirmar cobro</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL COBRAR CUOTA DE INSCRIPCIÓN */}
      {cobrarCIModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
          onClick={() => setCobrarCIModal(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: "12px", padding: "24px", width: "340px", boxShadow: "0 8px 30px rgba(0,0,0,0.2)" }}>
            <h3 style={{ margin: "0 0 4px", fontSize: "15px", fontWeight: 700, color: "#1e293b" }}>Cobrar cuota de inscripción</h3>
            <p style={{ margin: "0 0 16px", fontSize: "12px", color: "#6b7280" }}>
              {cobrarCIModal.concepto} — {cobrarCIModal.importe.toFixed(2)} EUR
            </p>
            <div style={{ marginBottom: "12px" }}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "4px" }}>Método de pago</label>
              <select value={ciMetodo} onChange={(e) => setCIMetodo(e.target.value)}
                style={{ width: "100%", padding: "8px", fontSize: "13px", border: "1px solid #e2e8f0", borderRadius: "6px" }}>
                <option value="efectivo">Efectivo</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="transferencia">Transferencia</option>
                <option value="bizum">Bizum</option>
              </select>
            </div>
            <div style={{ marginBottom: "12px" }}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "4px" }}>Fecha de cobro</label>
              <input type="date" value={ciFecha} onChange={(e) => setCIFecha(e.target.value)}
                style={{ width: "100%", padding: "8px", fontSize: "13px", border: "1px solid #e2e8f0", borderRadius: "6px" }} />
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", fontWeight: 500, color: "#374151", cursor: "pointer" }}>
                <input type="checkbox" checked={ciEmitirFactura} onChange={(e) => setCIEmitirFactura(e.target.checked)} />
                Emitir también factura
              </label>
            </div>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button onClick={() => setCobrarCIModal(null)} disabled={processing}
                style={{ padding: "8px 16px", fontSize: "13px", border: "1px solid #e2e8f0", borderRadius: "6px", background: "#fff", cursor: processing ? "not-allowed" : "pointer" }}>Cancelar</button>
              <button onClick={handleCobrarCI} disabled={processing}
                style={{ padding: "8px 16px", fontSize: "13px", border: "none", borderRadius: "6px", background: "#16a34a", color: "#fff", fontWeight: 600, cursor: processing ? "not-allowed" : "pointer", opacity: processing ? 0.5 : 1 }}>
                {processing ? "..." : "Cobrar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL EDITAR COBRO */}
      {editarCobro && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
          onClick={() => setEditarCobro(null)}>
          <div style={{ background: "#fff", borderRadius: "10px", padding: "20px", width: "340px", maxWidth: "90vw", boxShadow: "0 16px 48px rgba(0,0,0,0.2)" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
              <div style={{ width: "40px", height: "40px", borderRadius: "50%", backgroundColor: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Banknote className="w-5 h-5" style={{ color: "#16a34a" }} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 700 }}>Editar cobro</h3>
                <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>{editarCobro.mes.mes_nombre} — {editarCobro.mes.importe?.toFixed(2)} EUR</p>
              </div>
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px", color: "#374151" }}>Metodo de pago</label>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {["efectivo", "tarjeta", "bizum", "transferencia", "domiciliacion"].map((m) => (
                  <label key={m} style={{
                    display: "flex", alignItems: "center", gap: "4px", padding: "4px 10px", cursor: "pointer", fontSize: "12px",
                    borderRadius: "4px", border: editarCobro.metodo_nuevo === m ? "2px solid var(--cn-primary)" : "1px solid #d1d5db",
                    backgroundColor: editarCobro.metodo_nuevo === m ? "#eff6ff" : "#fff", fontWeight: editarCobro.metodo_nuevo === m ? 600 : 400,
                  }}>
                    <input type="radio" name="editar_metodo" value={m} checked={editarCobro.metodo_nuevo === m}
                      onChange={() => setEditarCobro({ ...editarCobro, metodo_nuevo: m })} style={{ display: "none" }} />
                    {MET_FULL[m] || MET[m] || m}
                  </label>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "4px" }}>Fecha de cobro</label>
              <input type="date" value={editarCobro.fecha_cobro_real || ""}
                onChange={(e) => setEditarCobro({ ...editarCobro, fecha_cobro_real: e.target.value })}
                style={{ width: "100%", padding: "8px", fontSize: "13px", border: "1px solid #e2e8f0", borderRadius: "6px" }} />
            </div>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button onClick={() => setEditarCobro(null)}
                style={{ padding: "6px 14px", fontSize: "12px", border: "1px solid #d1d5db", borderRadius: "5px", background: "#fff", cursor: "pointer" }}>
                Cancelar
              </button>
              <button onClick={handleEditarCobro} disabled={processing || (editarCobro.metodo_nuevo === editarCobro.metodo_actual && !editarCobro.fecha_cobro_real)}
                style={{ padding: "6px 14px", fontSize: "12px", fontWeight: 600, border: "none", borderRadius: "5px", background: "var(--cn-primary)", color: "#fff",
                  cursor: processing ? "not-allowed" : "pointer",
                  opacity: processing ? 0.5 : 1 }}>
                {processing ? "..." : "Guardar cambio"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CONFIRMAR EMISION DE FACTURA */}
      {confirmEmitir && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
          onClick={() => setConfirmEmitir(null)}>
          <div style={{ background: "#fff", borderRadius: "10px", padding: "24px", width: "400px", maxWidth: "90vw", boxShadow: "0 16px 48px rgba(0,0,0,0.2)" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
              <div style={{ width: "44px", height: "44px", borderRadius: "50%", backgroundColor: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <FileText className="w-5 h-5" style={{ color: "#16a34a" }} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 700 }}>Emitir factura</h3>
                <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>{confirmEmitir.mes.mes_nombre} {anyo}</p>
              </div>
            </div>
            <div style={{ padding: "12px", backgroundColor: "#f9fafb", borderRadius: "8px", marginBottom: "16px", fontSize: "13px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                <span style={{ color: "#6b7280" }}>Concepto:</span>
                <span style={{ fontWeight: 600 }}>{confirmEmitir.mes.concepto}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#6b7280" }}>Importe:</span>
                <span style={{ fontWeight: 700 }}>{confirmEmitir.mes.importe.toFixed(2)} EUR</span>
              </div>
            </div>
            <p style={{ fontSize: "12px", color: "#991b1b", marginBottom: "16px", fontWeight: 500 }}>
              Esta accion es irreversible. Se emitira una factura con numero SIF definitivo.
            </p>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button onClick={() => setConfirmEmitir(null)}
                style={{ padding: "8px 16px", fontSize: "13px", border: "1px solid #d1d5db", borderRadius: "6px", background: "#fff", cursor: "pointer" }}>
                Cancelar
              </button>
              <button onClick={handleConfirmEmitir} disabled={processing}
                style={{ padding: "8px 16px", fontSize: "13px", fontWeight: 600, border: "none", borderRadius: "6px", background: "#16a34a", color: "#fff", cursor: processing ? "wait" : "pointer", opacity: processing ? 0.7 : 1 }}>
                {processing ? "Emitiendo..." : "Emitir factura"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL · GENERAR EL AÑO DE ESTE CLIENTE (Ann 2026-10-09)
          Los números que se pintan aquí son los que ha devuelto el SERVIDOR en el
          simulacro, no una cuenta hecha en la pantalla. */}
      {generarModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
          onClick={() => setGenerarModal(null)}>
          <div style={{ background: "#fff", borderRadius: "10px", padding: "24px", width: "430px", maxWidth: "90vw", boxShadow: "0 16px 48px rgba(0,0,0,0.2)" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
              <div style={{ width: "44px", height: "44px", borderRadius: "50%", backgroundColor: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Banknote className="w-5 h-5" style={{ color: "#2563eb" }} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 700 }}>Generar el año {anyo}</h3>
                <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
                  {generarModal.cliente_nombre || "Este cliente"}
                </p>
              </div>
            </div>

            {generarModal.motivo_sin_cobros ? (
              <p style={{ fontSize: "13px", color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "8px", padding: "12px", margin: "0 0 16px" }}>
                {generarModal.motivo_sin_cobros}
              </p>
            ) : (
              <>
                <div style={{ padding: "12px", backgroundColor: "#f9fafb", borderRadius: "8px", marginBottom: "14px", fontSize: "13px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                    <span style={{ color: "#6b7280" }}>Se van a crear:</span>
                    <span style={{ fontWeight: 700 }}>
                      {generarModal.total_cobros_creados} {generarModal.total_cobros_creados === 1 ? "cuota" : "cuotas"}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                    <span style={{ color: "#6b7280" }}>Ya existían (no se tocan):</span>
                    <span style={{ fontWeight: 600 }}>{generarModal.total_cobros_saltados}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "#6b7280" }}>Suscripciones:</span>
                    <span style={{ fontWeight: 600 }}>{generarModal.total_suscripciones}</span>
                  </div>
                </div>
                {(generarModal.motivos_saltados || []).length > 0 && (
                  <div style={{ padding: "10px 12px", backgroundColor: "#fffbeb", border: "1px solid #fde68a", borderRadius: "8px", marginBottom: "14px", fontSize: "12px", color: "#92400e" }}>
                    <div style={{ fontWeight: 700, marginBottom: "4px" }}>Meses que no se crean</div>
                    {generarModal.motivos_saltados.map((m) => (
                      <div key={m.motivo}>· {m.meses} {m.meses === 1 ? "mes" : "meses"} — {m.motivo}</div>
                    ))}
                  </div>
                )}
                <p style={{ fontSize: "12px", color: "#6b7280", margin: "0 0 16px", lineHeight: 1.5 }}>
                  No se cobra nada ahora: se crean las cuotas previstas del año, igual
                  que en la apertura anual. Cada cuota nace con la forma de pago de su
                  suscripción. Los meses que ya tengan cuota no se duplican, y no se
                  crean meses posteriores a una baja.
                </p>
              </>
            )}

            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button onClick={() => setGenerarModal(null)}
                style={{ padding: "8px 16px", fontSize: "13px", border: "1px solid #d1d5db", borderRadius: "6px", background: "#fff", cursor: "pointer" }}>
                {generarModal.motivo_sin_cobros ? "Cerrar" : "Cancelar"}
              </button>
              {!generarModal.motivo_sin_cobros && generarModal.total_cobros_creados > 0 && (
                <button onClick={generarAnyoDeVerdad} disabled={generando}
                  style={{ padding: "8px 16px", fontSize: "13px", fontWeight: 600, border: "none", borderRadius: "6px", background: "#2563eb", color: "#fff", cursor: generando ? "wait" : "pointer", opacity: generando ? 0.7 : 1 }}>
                  {generando ? "Creando..." : `Crear las ${generarModal.total_cobros_creados}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL CONFIRMAR ANULACION DE FACTURA */}
      {confirmAnular && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
          onClick={() => setConfirmAnular(null)}>
          <div style={{ background: "#fff", borderRadius: "10px", padding: "24px", width: "420px", maxWidth: "90vw", boxShadow: "0 16px 48px rgba(0,0,0,0.25)" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
              <div style={{ width: "48px", height: "48px", borderRadius: "50%", backgroundColor: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <XCircle className="w-6 h-6" style={{ color: "#dc2626" }} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "#dc2626" }}>Anulacion de factura</h3>
                <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
                  {confirmAnular.mes.concepto}
                </p>
              </div>
            </div>
            <div style={{ padding: "14px", backgroundColor: "#fef2f2", borderRadius: "8px", marginBottom: "16px", border: "1px solid #fecaca" }}>
              <p style={{ margin: "0 0 8px", fontSize: "13px", fontWeight: 700, color: "#991b1b" }}>
                Esta accion es irreversible
              </p>
              <p style={{ margin: "0 0 6px", fontSize: "12px", color: "#991b1b", lineHeight: "1.5" }}>
                Se emitira una factura rectificativa que se registrara directamente en los archivos de Verifactu (SIF) y en la Agencia Tributaria. El cobro asociado sera anulado.
              </p>
              <p style={{ margin: 0, fontSize: "12px", color: "#991b1b", fontWeight: 600 }}>
                Este documento fiscal no se puede deshacer una vez confirmado.
              </p>
            </div>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button onClick={() => setConfirmAnular(null)}
                style={{ padding: "8px 16px", fontSize: "13px", border: "1px solid #d1d5db", borderRadius: "6px", background: "#fff", cursor: "pointer" }}>
                Cancelar
              </button>
              <button onClick={handleConfirmAnular} disabled={processing}
                style={{ padding: "8px 16px", fontSize: "13px", fontWeight: 600, border: "none", borderRadius: "6px", background: "#dc2626", color: "#fff", cursor: processing ? "wait" : "pointer", opacity: processing ? 0.7 : 1 }}>
                {processing ? "Procesando..." : "Confirmar anulacion"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CONFIRMAR CAMBIO DE IMPORTE */}
      {confirmImporte && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
          onClick={() => setConfirmImporte(null)}>
          <div style={{ background: "#fff", borderRadius: "10px", padding: "24px", width: "380px", maxWidth: "90vw", boxShadow: "0 16px 48px rgba(0,0,0,0.2)" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
              <div style={{ width: "40px", height: "40px", borderRadius: "50%", backgroundColor: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Pencil className="w-5 h-5" style={{ color: "#dc2626" }} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 700 }}>Modificacion de cuota</h3>
                <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>Este cambio afectara al cobro y facturacion</p>
              </div>
            </div>
            <div style={{ padding: "12px", backgroundColor: "#f9fafb", borderRadius: "8px", marginBottom: "16px", fontSize: "13px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                <span style={{ color: "#6b7280" }}>Importe original:</span>
                <span style={{ fontWeight: 600 }}>{confirmImporte.original.toFixed(2)} EUR</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                <span style={{ color: "#6b7280" }}>Nuevo importe:</span>
                <span style={{ fontWeight: 700, color: "#dc2626" }}>{confirmImporte.value.toFixed(2)} EUR</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #e5e7eb", paddingTop: "6px" }}>
                <span style={{ color: "#6b7280" }}>Diferencia:</span>
                <span style={{ fontWeight: 600, color: confirmImporte.value > confirmImporte.original ? "#dc2626" : "#16a34a" }}>
                  {confirmImporte.value > confirmImporte.original ? "+" : ""}{(confirmImporte.value - confirmImporte.original).toFixed(2)} EUR
                </span>
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button onClick={() => setConfirmImporte(null)}
                style={{ padding: "8px 16px", fontSize: "13px", border: "1px solid #d1d5db", borderRadius: "6px", background: "#fff", cursor: "pointer" }}>
                Cancelar
              </button>
              <button onClick={handleConfirmImporte}
                style={{ padding: "8px 16px", fontSize: "13px", fontWeight: 600, border: "none", borderRadius: "6px", background: "#dc2626", color: "#fff", cursor: "pointer" }}>
                Confirmar cambio
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL PREVIEW RECIBO/FACTURA — Arquitectura idéntica a FacturasEmitidasB2BTab */}
      {previewModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
          onClick={() => { setPreviewModal(null); setPreviewHtml(""); }}>
          <div style={{ background: "#f3f4f6", borderRadius: "12px", width: "800px", maxWidth: "95vw", maxHeight: "95vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden" }}
            onClick={(e) => e.stopPropagation()}>

            {/* PDF Preview area */}
            <div style={{ flex: 1, padding: "24px", minHeight: "500px", display: "flex", flexDirection: "column" }}>
              {loadingPreview ? (
                <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "12px", color: "#6b7280" }}>
                  <Loader2 className="w-8 h-8" style={{ animation: "spin 1s linear infinite" }} />
                  <p style={{ margin: 0, fontSize: "13px" }}>Generando vista previa...</p>
                </div>
              ) : previewHtml && previewHtml.startsWith("HTML:") ? (
                <div style={{ flex: 1, background: "#fff", borderRadius: "8px", border: "1px solid #e5e7eb", overflow: "auto" }}
                  dangerouslySetInnerHTML={{ __html: previewHtml.slice(5) }} />
              ) : previewHtml ? (
                <iframe
                  src={previewHtml}
                  style={{ flex: 1, width: "100%", minHeight: "600px", border: "none", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}
                  title={`Vista Previa - ${previewModal.type}`}
                />
              ) : null}
            </div>

            {/* Action buttons — idénticos a FacturasEmitidasB2BTab */}
            {!loadingPreview && previewHtml && (
              <div style={{ padding: "12px 24px 20px", display: "flex", justifyContent: "center", gap: "12px", flexWrap: "wrap" }}>
                <button onClick={handlePrint}
                  style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", fontSize: "13px", fontWeight: 500, border: "1px solid #d1d5db", borderRadius: "8px", background: "#fff", cursor: "pointer", color: "#374151" }}>
                  <Printer className="w-4 h-4" />
                  Imprimir {previewModal.type === "recibo" ? "Recibo" : "Factura"}
                </button>
                {previewModal.type !== "recibo" && (
                  <button onClick={handlePrintMutua}
                    style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", fontSize: "13px", fontWeight: 500, border: "1px solid #5eead4", borderRadius: "8px", background: "#fff", cursor: "pointer", color: "#0f766e" }}>
                    <Printer className="w-4 h-4" />
                    Imprimir para Mutua
                  </button>
                )}
                <button onClick={() => handleDownloadPDF(previewModal.id, previewModal.type)}
                  style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", fontSize: "13px", fontWeight: 500, border: "1px solid #d1d5db", borderRadius: "8px", background: "#fff", cursor: "pointer", color: "#374151" }}>
                  <Download className="w-4 h-4" />
                  Descargar PDF
                </button>
                <button
                  onClick={() => previewModal.type === "recibo" ? handleSendEmailRecibo(previewModal.id) : handleSendEmailFactura(previewModal.id)}
                  disabled={sendingEmail === previewModal.id}
                  style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", fontSize: "13px", fontWeight: 600, border: "none", borderRadius: "8px", background: "#7c3aed", color: "#fff", cursor: sendingEmail === previewModal.id ? "wait" : "pointer", opacity: sendingEmail === previewModal.id ? 0.7 : 1 }}>
                  {sendingEmail === previewModal.id ? <Loader2 className="w-4 h-4" style={{ animation: "spin 1s linear infinite" }} /> : <Mail className="w-4 h-4" />}
                  Enviar por Email
                </button>
                <button onClick={() => { setPreviewModal(null); setPreviewHtml(""); }}
                  style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", fontSize: "13px", fontWeight: 500, border: "1px solid #d1d5db", borderRadius: "8px", background: "#fff", cursor: "pointer", color: "#6b7280" }}>
                  Cerrar
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MODAL COBRAR */}
      {cobrarModal && (
        <div
          style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
          onClick={() => setCobrarModal(null)}
        >
          <div
            style={{ background: "#fff", borderRadius: "10px", padding: "20px", width: "340px", maxWidth: "90vw", boxShadow: "0 16px 48px rgba(0,0,0,0.2)" }}
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const esDevuelto = cobrarModal.mes.estado === "devuelto";
              const dev = cobrarModal.mes.devolucion;
              return (<>
                <h3 style={{ margin: "0 0 2px", fontSize: "14px", fontWeight: 700 }}>
                  {esDevuelto ? "Re-cobrar devolución" : "Cobrar"} - {cobrarModal.mes.mes_nombre} {anyo}
                </h3>
                <p style={{ margin: "0 0 12px", fontSize: "12px", color: "#6b7280" }}>
                  {cobrarModal.sub.concepto} - {cobrarModal.mes.importe.toFixed(2)} EUR
                </p>

                {esDevuelto && dev && (
                  <div style={{ padding: "8px 10px", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "6px", marginBottom: "12px", fontSize: "12px" }}>
                    <div style={{ fontWeight: 700, color: "#dc2626", marginBottom: "3px" }}>
                      Devolución: {dev.codigo} — {dev.motivo}
                    </div>
                    <div style={{ color: "#991b1b", fontSize: "11px" }}>Fecha devolución: {dev.fecha} · Estado: {dev.estado_gestion}</div>
                  </div>
                )}

                <div style={{ marginBottom: "12px" }}>
                  <label style={{ display: "block", fontSize: "11px", fontWeight: 600, marginBottom: "4px", color: "#374151" }}>Fecha de pago</label>
                  <input type="date" value={cobrarFecha} onChange={(e) => setCobrarFecha(e.target.value)}
                    style={{ width: "100%", padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: "5px", fontSize: "13px", boxSizing: "border-box" }} />
                </div>

                <div style={{ marginBottom: "12px" }}>
                  <label style={{ display: "block", fontSize: "11px", fontWeight: 600, marginBottom: "4px", color: "#374151" }}>Método de pago</label>
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    {["efectivo", "tarjeta", "bizum", "transferencia", "domiciliacion"].map((m) => (
                      <label key={m} style={{
                        display: "flex", alignItems: "center", gap: "4px", padding: "4px 10px", cursor: "pointer", fontSize: "12px",
                        borderRadius: "4px", border: cobrarMetodo === m ? "2px solid var(--cn-primary)" : "1px solid #d1d5db",
                        backgroundColor: cobrarMetodo === m ? "#eff6ff" : "#fff", fontWeight: cobrarMetodo === m ? 600 : 400,
                      }}>
                        <input type="radio" name="metodo" value={m} checked={cobrarMetodo === m} onChange={() => setCobrarMetodo(m)} style={{ display: "none" }} />
                        {m === "domiciliacion" ? "Re-domiciliar (SEPA)" : MET_FULL[m]}
                      </label>
                    ))}
                  </div>
                  {cobrarMetodo === "domiciliacion" && (
                    <div style={{ marginTop: "6px", padding: "6px 8px", backgroundColor: "#eff6ff", borderRadius: "4px", fontSize: "11px", color: "#1e40af" }}>
                      Se incluirá en la próxima remesa SEPA. El cobro quedará como pendiente hasta que el banco confirme.
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: "12px", display: "flex", gap: "10px" }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: "block", fontSize: "11px", fontWeight: 600, marginBottom: "4px", color: "#374151" }}>Cuota</label>
                    <input type="number" step="0.01" value={cobrarImporte} onChange={(e) => setCobrarImporte(parseFloat(e.target.value) || 0)}
                      style={{ width: "100%", padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: "5px", fontSize: "13px", fontWeight: 600, boxSizing: "border-box" }} />
                  </div>
                  {esDevuelto && (
                    <div style={{ width: "100px" }}>
                      <label style={{ display: "block", fontSize: "11px", fontWeight: 600, marginBottom: "4px", color: "#dc2626" }}>Gastos</label>
                      <input type="number" step="0.01" value={cobrarGastos} onChange={(e) => setCobrarGastos(parseFloat(e.target.value) || 0)}
                        style={{ width: "100%", padding: "6px 10px", border: "1px solid #fecaca", borderRadius: "5px", fontSize: "13px", fontWeight: 600, boxSizing: "border-box", backgroundColor: "#fef2f2" }} />
                    </div>
                  )}
                </div>

                {esDevuelto && (
                  <div style={{ marginBottom: "12px", padding: "6px 10px", backgroundColor: "#f8fafc", borderRadius: "4px", fontSize: "13px", fontWeight: 700, textAlign: "right" }}>
                    Total a cobrar: {(cobrarImporte + cobrarGastos).toFixed(2)}€
                  </div>
                )}


                <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                  <button onClick={() => setCobrarModal(null)} style={{ padding: "6px 14px", fontSize: "12px", border: "1px solid #d1d5db", borderRadius: "5px", background: "#fff", cursor: "pointer" }}>
                    Cancelar
                  </button>
                  <button onClick={handleCobrar} disabled={processing}
                    style={{ padding: "6px 14px", fontSize: "12px", fontWeight: 600, border: "none", borderRadius: "5px",
                      background: esDevuelto ? "#dc2626" : "var(--cn-primary)", color: "#fff",
                      cursor: processing ? "wait" : "pointer", opacity: processing ? 0.7 : 1 }}>
                    {processing ? "..." : esDevuelto ? `Re-cobrar ${(cobrarImporte + cobrarGastos).toFixed(2)}€` : "Confirmar cobro"}
                  </button>
                </div>
              </>);
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default RejillaAnualPagos;
